import React, { useState } from 'react';
import styles from './DocumentListVault.module.scss';
import { Box, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { Helpers } from '../../../../common/helpers/helpers';
import { useDocumentsFunctions } from '../../hooks/useDocumentsFunctions';
import EmployeeIcon from '@mui/icons-material/BadgeOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CustomersIcon from '@mui/icons-material/SupportAgentOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { DocumentsListRowProps } from './types';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../../../common/constants/routes';
import { useDocumensoRedirect } from '../../../../common/hooks/useDocumensoRedirect';

/** A document added less than 5 minutes ago gets the "New" badge. */
const NEW_DOCUMENT_PERIOD = 300000;
/** End dates closer than this are highlighted as "getting closer". */
const GETTING_CLOSER_DAYS = 30;

/** File type badge colours: PDF red, Word blue, images green, Business Kitz purple. */
const FILE_TYPE_VARIANTS: Record<string, string> = {
  pdf: styles.file_badge_pdf,
  doc: styles.file_badge_word,
  docx: styles.file_badge_word,
  png: styles.file_badge_image,
  jpg: styles.file_badge_image,
  jpeg: styles.file_badge_image,
  heic: styles.file_badge_image,
  bk: styles.file_badge_bk,
};

const IMAGE_FILE_TYPES = ['png', 'jpg', 'jpeg', 'heic'];

/**
 * Key Dates row, same layout as the Document Vault one: file type badge, the document name with
 * the assignee and the workflow underneath, the start and end / renewal dates (end date coloured
 * when it has passed or is within 30 days, as before) and the action buttons.
 */
export const DocumentsListRow = ({ doc, renderActionButtons }: DocumentsListRowProps) => {
  const { t } = useTranslation();
  const {
    getWorkflow,
    getFileTypeFromName,
    getProfileName,
    getProfileType,
    navigateToDocumentPageTab,
  } = useDocumentsFunctions();
  const { redirectToDocumenso } = useDocumensoRedirect();
  const path = location.pathname;

  const renderProfileIcon = (profileType: string) => {
    switch (profileType) {
      case 'employment':
        return <EmployeeIcon fontSize='inherit' />;
      case 'supplier':
        return <BusinessCenterOutlinedIcon fontSize='inherit' />;
      case 'customer':
        return <CustomersIcon fontSize='inherit' />;
      case 'business':
        return <ApartmentOutlinedIcon fontSize='inherit' />;
      default:
        return null;
    }
  };

  /** Shown instead of the assignee name when nobody is assigned. */
  const getProfileTypeLabel = (profileType: string) => {
    switch (profileType) {
      case 'employment':
        return t('labels.employee');
      case 'supplier':
        return t('labels.supplier');
      case 'customer':
        return t('labels.customer');
      case 'business':
        return t('tagsLabels.categories.business');
      default:
        return '';
    }
  };

  const getFileTypeTooltip = (fileType: string) => {
    if (fileType === 'pdf') return t('fileTypes.pdf');
    if (fileType === 'doc' || fileType === 'docx') return t('fileTypes.msWord');
    if (fileType === 'bk') return t('fileTypes.businessKitz');
    if (IMAGE_FILE_TYPES.includes(fileType)) return t('fileTypes.image');
    return fileType.toUpperCase();
  };

  const profileType = getProfileType(doc.category ?? '');
  const profileIcon = renderProfileIcon(profileType);
  const assignedNames = getProfileName(profileType, doc).filter((name) => name && name !== '-');
  const profileText =
    assignedNames.length > 0 ? assignedNames.join(', ') : getProfileTypeLabel(profileType);
  const workflow = getWorkflow(doc);
  const fileType = (
    doc.du_filetype && doc.du_filetype.length > 0
      ? doc.du_filetype[0]
      : getFileTypeFromName(doc.name ?? '', doc.contract_generation as number)
  )?.toLowerCase();
  const [now] = useState(() => Date.now());
  const isNew = now - new Date(doc.created ?? 0).getTime() < NEW_DOCUMENT_PERIOD;
  const startDate = doc.extras?.[0]?.start_date;
  const endDate = doc.extras?.[0]?.end_date;
  const endDateStatus = endDate
    ? new Date(endDate).getTime() < now
      ? styles.overdue
      : (new Date(endDate).getTime() - now) / 1000 / 60 / 60 / 24 < GETTING_CLOSER_DAYS
        ? styles.getting_closer
        : ''
    : '';
  const documentName =
    doc.name?.endsWith('.pdf') || doc.name?.endsWith('.docx') || doc.name?.endsWith('.doc')
      ? doc.name.replace('.pdf', '').replace('.docx', '').replace('.doc', '')
      : doc.name;

  const navigateToDocument = () => {
    if (doc?.signing_doc_id) {
      redirectToDocumenso(
        `${ROUTES.DOCUMENTS}/${doc?.signing_doc_id}?view=true&backUrl=${path}&activeTab=${doc.category}`
      );
    } else {
      navigateToDocumentPageTab(doc, 1);
    }
  };

  return (
    <TableRow
      className={`${styles.table_row} ${styles.compact_row} ${isNew ? styles.doc_new : ''}`}
    >
      <TableCell
        id={`documentsKeyDatesDocumentsListRowNavigateToDocumentPageTabCell_${doc?.id}`}
        className={`${styles.table_cell} ${styles.compact_name_cell}`}
        onClick={navigateToDocument}
      >
        <Box
          id={`documentsKeyDatesDocumentsListRowNavigateToDocumentPageTabCellBox_${doc?.id}`}
          className={styles.compact_name_container}
        >
          {fileType && (
            <Tooltip title={getFileTypeTooltip(fileType)} arrow placement='top'>
              <Box
                id={`documentsKeyDatesDocumentsListRowFileTypeBadge_${doc?.id}`}
                className={`${styles.file_badge} ${FILE_TYPE_VARIANTS[fileType] ?? ''}`}
              >
                {fileType}
              </Box>
            </Tooltip>
          )}
          <Box className={styles.compact_text_container}>
            <Box
              id={`documentsKeyDatesDocumentsListRowNavigateToDocumentPageTabCellBox2_${doc?.id}`}
              className={styles.compact_title_line}
            >
              <Typography component='span' className={styles.compact_title}>
                {documentName}
              </Typography>
              {isNew && (
                <Box
                  id={`documentsKeyDatesDocumentsListRowNavigateToDocumentPageTabCellBox3_${doc?.id}`}
                  className={styles.badge_new}
                >
                  {t('labels.new')}
                </Box>
              )}
            </Box>
            {(profileText || workflow !== '-') && (
              <Typography
                id={`documentsKeyDatesDocumentsListRowSubtitle_${doc?.id}`}
                component='span'
                className={styles.compact_subtitle}
              >
                {profileText && (
                  <span
                    id={`documentsKeyDatesDocumentsListRowNavigateToDocumentPageTabCell2Text_${doc?.id}`}
                    className={styles.compact_profile}
                  >
                    {profileIcon && (
                      <span className={styles.compact_profile_icon} aria-hidden='true'>
                        {profileIcon}
                      </span>
                    )}
                    {profileText}
                  </span>
                )}
                {profileText && workflow !== '-' && (
                  <span className={styles.compact_separator} aria-hidden='true'>
                    &middot;
                  </span>
                )}
                {workflow !== '-' && (
                  <span
                    id={`documentsKeyDatesDocumentsListRowNavigateToDocumentPageTabCell3Text_${doc?.id}`}
                  >
                    {workflow}
                  </span>
                )}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell
        id={`documentsKeyDatesDocumentsListRowNavigateToDocumentPageTabCell4_${doc?.id}`}
        className={`${styles.table_cell} ${styles.compact_dates_cell}`}
        onClick={navigateToDocument}
      >
        <Box className={styles.compact_dates}>
          {startDate && (
            <Tooltip title={t('labels.startDate')} arrow placement='top'>
              <Typography
                id={`documentsKeyDatesDocumentsListRowNavigateToDocumentPageTabCell4Text_${doc?.id}`}
                component='span'
                className={styles.compact_date}
              >
                <span className={styles.compact_date_label}>{t('documentsTable.starts')}</span>{' '}
                {Helpers.timeStampToDateV2(startDate)}
              </Typography>
            </Tooltip>
          )}
          {endDate && (
            <Tooltip title={t('labels.endRenewalDate')} arrow placement='top'>
              <Typography
                id={`documentsKeyDatesDocumentsListRowNavigateToDocumentPageTabCell5Text_${doc?.id}`}
                component='span'
                className={`${styles.compact_date} ${styles.compact_date_end} ${endDateStatus}`}
              >
                <span className={styles.compact_date_label}>{t('documentsTable.endsRenews')}</span>{' '}
                {Helpers.timeStampToDateV2(doc.key_date_end_date)}
              </Typography>
            </Tooltip>
          )}
          {!startDate && !endDate && (
            <Typography component='span' className={styles.compact_date}>
              -
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell className={`${styles.table_cell} ${styles.compact_actions_cell}`}>
        {renderActionButtons(doc)}
      </TableCell>
    </TableRow>
  );
};
